/**
 * Applications page — track job applications with inline editing.
 *
 * Features:
 *  - List of all applications sorted by applied date (newest first)
 *  - Filter by last action status and applied-after date
 *  - Inline editing of every field (click to edit, Enter/blur to save)
 *  - "Add Application" dialog to create new entries
 *  - "Add Action" button per row to append a new status event
 *  - Company popover (Building2 icon) with portal URL management and per-company stats
 *  - Delete with confirmation
 */
import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Pencil,
  Building2,
  Trash2,
  Loader2,
  SlidersHorizontal,
  X,
  ClipboardList,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Link,
} from 'lucide-react';
import {
  applicationApi,
  companyApi,
  authApi,
  APPLICATION_ACTION_LABELS,
} from '@/lib/api';
import type {
  Application,
  ApplicationAction,
  ApplicationEvent,
  CreateApplication,
  UpdateApplicationEvent,
} from '@/lib/api';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ApplicationSankey } from '@/components/ApplicationSankey';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Navigation } from '@/components/Navigation';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

// ── Constants ──────────────────────────────────────────────────────────────────

const ALL_ACTIONS = Object.keys(APPLICATION_ACTION_LABELS) as ApplicationAction[];

function actionLabel(action: ApplicationAction): string {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return APPLICATION_ACTION_LABELS[action]!;
}

const ACTION_BADGE_CLASS: Readonly<Record<ApplicationAction, string>> = {
  applied:             'border-blue-300 text-blue-700 bg-blue-50',
  phone_screen:        'border-purple-300 text-purple-700 bg-purple-50',
  interview:           'border-purple-300 text-purple-700 bg-purple-50',
  technical_interview: 'border-purple-300 text-purple-700 bg-purple-50',
  offer:               'border-green-300 text-green-700 bg-green-50',
  accepted:            'border-green-400 text-green-800 bg-green-100',
  rejected:            'border-red-300 text-red-700 bg-red-50',
  declined:            'border-orange-300 text-orange-700 bg-orange-50',
  ghosted:             'border-gray-300 text-gray-600 bg-gray-50',
  withdrawn:           'border-orange-300 text-orange-700 bg-orange-50',
};

function today(): string {
  return new Date().toISOString().split('T')[0]!;
}

function formatDate(iso: string): string {
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(year!, month! - 1, day!).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// ── Inline editable cell ───────────────────────────────────────────────────────

interface EditableCellProps {
  value: string;
  onSave: (val: string) => void;
  type?: 'text' | 'date';
  placeholder?: string;
  className?: string;
}

function EditableCell({ value, onSave, type = 'text', placeholder, className }: EditableCellProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  const commit = () => {
    setEditing(false);
    const trimmed = draft.trim();
    if (trimmed !== value && trimmed !== '') onSave(trimmed);
    else if (type === 'date' && draft !== value) onSave(draft);
    else setDraft(value);
  };

  const cancel = () => {
    setEditing(false);
    setDraft(value);
  };

  if (!editing) {
    return (
      <span
        role="button"
        tabIndex={0}
        onClick={() => setEditing(true)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setEditing(true); }}
        title="Click to edit"
        className={`cursor-pointer rounded px-1 -mx-1 hover:bg-muted/60 transition-colors ${className ?? ''}`}
      >
        {type === 'date' ? formatDate(value) : (value || <span className="text-muted-foreground italic">{placeholder}</span>)}
      </span>
    );
  }

  return (
    <input
      ref={inputRef}
      type={type}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') commit();
        if (e.key === 'Escape') cancel();
      }}
      className="w-full rounded border border-primary/40 bg-background px-1.5 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
    />
  );
}

// ── Action history popover ─────────────────────────────────────────────────────

interface EventRowProps {
  event: ApplicationEvent;
  isOnly: boolean;
  onUpdate: (data: UpdateApplicationEvent) => void;
  onDelete: () => void;
}

function EventRow({ event, isOnly, onUpdate, onDelete }: EventRowProps) {
  const [action, setAction] = useState<ApplicationAction>(event.action);
  const [date, setDate] = useState(event.eventDate);

  useEffect(() => {
    setAction(event.action);
    setDate(event.eventDate);
  }, [event.action, event.eventDate]);

  return (
    <div className="flex items-center gap-1.5">
      <select
        value={action}
        onChange={(e) => {
          const next = e.target.value as ApplicationAction;
          setAction(next);
          onUpdate({ action: next });
        }}
        className="flex-1 rounded border border-input bg-background px-1.5 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
      >
        {ALL_ACTIONS.map((a) => (
          <option key={a} value={a}>{actionLabel(a)}</option>
        ))}
      </select>
      <input
        type="date"
        value={date}
        onChange={(e) => setDate(e.target.value)}
        onBlur={() => { if (date !== event.eventDate) onUpdate({ eventDate: date }); }}
        onKeyDown={(e) => { if (e.key === 'Enter') onUpdate({ eventDate: date }); }}
        className="w-32 rounded border border-input bg-background px-1.5 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
      />
      <button
        onClick={onDelete}
        disabled={isOnly}
        title={isOnly ? 'Cannot delete the only event' : 'Delete event'}
        className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive disabled:pointer-events-none disabled:opacity-30"
      >
        <Trash2 className="h-3 w-3" />
      </button>
    </div>
  );
}

interface ActionHistoryPopoverProps {
  application: Application;
}

function ActionHistoryPopover({ application }: ActionHistoryPopoverProps) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const { data: events = [], isLoading } = useQuery({
    queryKey: ['events', application.id],
    queryFn: () => applicationApi.listEvents(application.id),
    enabled: open,
  });

  const updateMutation = useMutation({
    mutationFn: ({ eventId, data }: { eventId: string; data: UpdateApplicationEvent }) =>
      applicationApi.updateEvent(application.id, eventId, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['events', application.id] });
      void queryClient.invalidateQueries({ queryKey: ['applications'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (eventId: string) => applicationApi.deleteEvent(application.id, eventId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['events', application.id] });
      void queryClient.invalidateQueries({ queryKey: ['applications'] });
    },
  });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="text-left space-y-0.5 group/status" title="Click to view action history">
          <Badge
            variant="outline"
            className={`text-xs pointer-events-none ${ACTION_BADGE_CLASS[application.lastAction] ?? ''}`}
          >
            {actionLabel(application.lastAction)}
          </Badge>
          <p className="text-xs text-muted-foreground group-hover/status:text-foreground transition-colors">
            {formatDate(application.lastActionDate)}
          </p>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="start">
        <p className="text-sm font-semibold mb-3">Action History</p>
        {isLoading ? (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" />
            Loading…
          </div>
        ) : (
          <div className="space-y-2">
            {events.map((event) => (
              <EventRow
                key={event.id}
                event={event}
                isOnly={events.length === 1}
                onUpdate={(data) => updateMutation.mutate({ eventId: event.id, data })}
                onDelete={() => deleteMutation.mutate(event.id)}
              />
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

// ── Company popover ────────────────────────────────────────────────────────────

interface CompanyPopoverProps {
  company: string;
  allApplications: Application[];
}

function CompanyPopover({ company, allApplications }: CompanyPopoverProps) {
  const queryClient = useQueryClient();
  const [editingUrl, setEditingUrl] = useState(false);
  const [urlDraft, setUrlDraft] = useState('');

  const companyApps = allApplications.filter((a) => a.company === company);

  const statsByAction = ALL_ACTIONS.reduce<Partial<Record<ApplicationAction, number>>>((acc, action) => {
    const count = companyApps.filter((a) => a.lastAction === action).length;
    if (count > 0) acc[action] = count;
    return acc;
  }, {});

  const { data: companyData, isLoading } = useQuery({
    queryKey: ['companies', company],
    queryFn: () => companyApi.get(company),
  });

  useEffect(() => {
    if (companyData) setUrlDraft(companyData.portalUrl ?? '');
  }, [companyData]);

  const upsertMutation = useMutation({
    mutationFn: () => companyApi.upsert(company, urlDraft.trim() || null),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['companies', company] });
      setEditingUrl(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => companyApi.delete(company),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['companies', company] });
      setUrlDraft('');
    },
  });

  const cancelEdit = () => {
    setEditingUrl(false);
    setUrlDraft(companyData?.portalUrl ?? '');
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className="inline-flex h-5 w-5 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground transition-colors shrink-0"
          title="Company details"
        >
          <Building2 className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="start">
        <p className="text-sm font-semibold">{company}</p>

        {/* Portal URL section */}
        <div className="mt-3">
          <p className="text-xs font-medium text-muted-foreground mb-1.5">Application Portal</p>

          {isLoading ? (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" />
              Loading…
            </div>
          ) : editingUrl ? (
            <div className="space-y-1.5">
              <Input
                type="url"
                value={urlDraft}
                onChange={(e) => setUrlDraft(e.target.value)}
                placeholder="https://…"
                className="h-7 text-xs"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') upsertMutation.mutate();
                  if (e.key === 'Escape') cancelEdit();
                }}
              />
              <div className="flex gap-1.5">
                <Button size="sm" onClick={() => upsertMutation.mutate()} disabled={upsertMutation.isPending} className="h-6 text-xs px-2">
                  Save
                </Button>
                <Button size="sm" variant="ghost" onClick={cancelEdit} className="h-6 text-xs px-2">
                  Cancel
                </Button>
              </div>
            </div>
          ) : companyData?.portalUrl ? (
            <div className="flex items-center gap-1">
              <a
                href={companyData.portalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 text-xs text-primary hover:underline truncate"
                title={companyData.portalUrl}
              >
                <Link className="inline h-3 w-3 mr-1 -mt-0.5" />
                {companyData.portalUrl}
              </a>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setEditingUrl(true)}
                className="h-6 w-6 p-0 shrink-0"
                title="Edit URL"
              >
                <Pencil className="h-3 w-3" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
                className="h-6 w-6 p-0 shrink-0 hover:text-destructive"
                title="Remove URL"
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          ) : (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setEditingUrl(true)}
              className="h-6 text-xs px-2 gap-1"
            >
              <Plus className="h-3 w-3" />
              Add portal URL
            </Button>
          )}
        </div>

        {/* Stats section */}
        <div className="mt-4 border-t pt-3">
          <p className="text-xs font-medium text-muted-foreground mb-2">
            {companyApps.length} application{companyApps.length !== 1 ? 's' : ''}
          </p>
          <div className="space-y-1">
            {(Object.entries(statsByAction) as [ApplicationAction, number][]).map(([action, count]) => (
              <div key={action} className="flex items-center justify-between">
                <Badge
                  variant="outline"
                  className={`text-xs py-0 ${ACTION_BADGE_CLASS[action] ?? ''}`}
                >
                  {actionLabel(action)}
                </Badge>
                <span className="text-xs font-medium">{count}</span>
              </div>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ── Add Action dialog ──────────────────────────────────────────────────────────

interface AddActionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  applicationId: string;
}

function AddActionDialog({ open, onOpenChange, applicationId }: AddActionDialogProps) {
  const queryClient = useQueryClient();
  const [action, setAction] = useState<ApplicationAction>('phone_screen');
  const [actionDate, setActionDate] = useState(today());
  const [error, setError] = useState('');

  const handleOpenChange = (next: boolean) => {
    if (next) {
      setAction('phone_screen');
      setActionDate(today());
      setError('');
    }
    onOpenChange(next);
  };

  const updateMutation = useMutation({
    mutationFn: () =>
      applicationApi.update(applicationId, {
        lastAction: action,
        lastActionDate: actionDate,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['applications'] });
      onOpenChange(false);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!actionDate) { setError('Date is required'); return; }
    setError('');
    updateMutation.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[360px]">
        <DialogHeader>
          <DialogTitle>Add Action</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          <div className="space-y-1.5">
            <Label htmlFor="aa-action">Action *</Label>
            <select
              id="aa-action"
              value={action}
              onChange={(e) => setAction(e.target.value as ApplicationAction)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {ALL_ACTIONS.map((a) => (
                <option key={a} value={a}>{actionLabel(a)}</option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="aa-date">Date *</Label>
            <Input
              id="aa-date"
              type="date"
              value={actionDate}
              onChange={(e) => setActionDate(e.target.value)}
            />
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={updateMutation.isPending}>
              {updateMutation.isPending ? (
                <>
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                  Saving…
                </>
              ) : 'Add Action'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Application row ────────────────────────────────────────────────────────────

function ApplicationRow({ application, allApplications }: { application: Application; allApplications: Application[] }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [addActionOpen, setAddActionOpen] = useState(false);

  const updateMutation = useMutation({
    mutationFn: (data: Partial<Application>) =>
      applicationApi.update(application.id, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['applications'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => applicationApi.delete(application.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['applications'] });
    },
  });

  const patch = (field: string, value: string) =>
    updateMutation.mutate({ [field]: value });

  return (
    <>
      <tr className="group border-b last:border-b-0 hover:bg-muted/30 transition-colors">
        {/* Company */}
        <td className="px-4 py-3 text-sm font-medium">
          <div className="flex items-center gap-1.5">
            <EditableCell
              value={application.company}
              onSave={(v) => patch('company', v)}
              placeholder="Company"
            />
            <CompanyPopover company={application.company} allApplications={allApplications} />
          </div>
        </td>

        {/* Job title */}
        <td className="px-4 py-3 text-sm">
          <EditableCell
            value={application.jobTitle}
            onSave={(v) => patch('jobTitle', v)}
            placeholder="Job title"
          />
        </td>

        {/* Applied date */}
        <td className="px-4 py-3 text-sm whitespace-nowrap">
          <EditableCell
            value={application.appliedDate}
            type="date"
            onSave={(v) => patch('appliedDate', v)}
          />
        </td>

        {/* Status (last action + date → full history popover) */}
        <td className="px-4 py-3">
          <ActionHistoryPopover application={application} />
        </td>

        {/* Notes */}
        <td className="px-4 py-3 text-sm max-w-[200px]">
          <EditableCell
            value={application.notes ?? ''}
            onSave={(v) => updateMutation.mutate({ notes: v || null })}
            placeholder="Add notes…"
            className="text-muted-foreground"
          />
        </td>

        {/* Posting link */}
        <td className="px-4 py-3 text-center">
          {application.postingUrl ? (
            <a
              href={application.postingUrl}
              target="_blank"
              rel="noopener noreferrer"
              title="View job posting"
              className="inline-flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            >
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          ) : application.postingId ? (
            <button
              onClick={() => void navigate('/postings')}
              title="View internal posting"
              className="inline-flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            >
              <ExternalLink className="h-3.5 w-3.5" />
            </button>
          ) : null}
        </td>

        {/* Actions: Add Action + Delete */}
        <td className="px-4 py-3 text-right">
          <div className="flex items-center justify-end gap-2">
            {!confirmDelete && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setAddActionOpen(true)}
                className="invisible group-hover:visible text-xs"
              >
                Add Action
              </Button>
            )}

            {!confirmDelete ? (
              <button
                onClick={() => setConfirmDelete(true)}
                className="invisible group-hover:visible inline-flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            ) : (
              <div className="flex items-center gap-1 text-xs">
                <span className="text-destructive">Delete?</span>
                <button
                  onClick={() => deleteMutation.mutate()}
                  className="font-semibold text-destructive hover:underline"
                >
                  Yes
                </button>
                <span className="text-muted-foreground">/</span>
                <button
                  onClick={() => setConfirmDelete(false)}
                  className="text-muted-foreground hover:underline"
                >
                  No
                </button>
              </div>
            )}
          </div>
        </td>
      </tr>

      <AddActionDialog
        open={addActionOpen}
        onOpenChange={setAddActionOpen}
        applicationId={application.id}
      />
    </>
  );
}

// ── Add Application dialog ─────────────────────────────────────────────────────

interface AddDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}

function AddApplicationDialog({ open, onOpenChange, onCreated }: AddDialogProps) {
  const queryClient = useQueryClient();
  const [postingUrl, setPostingUrl] = useState('');
  const [company, setCompany] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [appliedDate, setAppliedDate] = useState(today());
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');

  const reset = () => {
    setPostingUrl('');
    setCompany('');
    setJobTitle('');
    setAppliedDate(today());
    setNotes('');
    setError('');
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const createMutation = useMutation({
    mutationFn: (data: CreateApplication) => applicationApi.create(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['applications'] });
      onCreated();
      onOpenChange(false);
      reset();
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!company.trim()) { setError('Company is required'); return; }
    if (!jobTitle.trim()) { setError('Job title is required'); return; }
    setError('');
    createMutation.mutate({
      postingUrl: postingUrl.trim() || undefined,
      company: company.trim(),
      jobTitle: jobTitle.trim(),
      appliedDate,
      lastAction: 'applied',
      lastActionDate: appliedDate,
      notes: notes.trim() || undefined,
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Add Application</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-2">

          {/* Optional posting URL */}
          <div className="space-y-1.5">
            <Label htmlFor="add-posting-url">Job Posting URL (optional)</Label>
            <Input
              id="add-posting-url"
              type="url"
              value={postingUrl}
              onChange={(e) => setPostingUrl(e.target.value)}
              placeholder="https://…"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="add-company">Company *</Label>
              <Input
                id="add-company"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                placeholder="Acme Corp"
                autoFocus
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="add-title">Job Title *</Label>
              <Input
                id="add-title"
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
                placeholder="Software Engineer"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="add-applied">Date Applied *</Label>
            <Input
              id="add-applied"
              type="date"
              value={appliedDate}
              onChange={(e) => setAppliedDate(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="add-notes">Notes</Label>
            <Input
              id="add-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes…"
            />
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending ? (
                <>
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                  Saving…
                </>
              ) : 'Add Application'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────

export default function ApplicationsPage() {
  const navigate = useNavigate();
  const [addOpen, setAddOpen] = useState(false);
  const [filterAction, setFilterAction] = useState<ApplicationAction | 'all'>('all');
  const [filterAfter, setFilterAfter] = useState('');
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('desc');
  const [sankeyOpen, setSankeyOpen] = useState(true);

  // ── Auth guard ─────────────────────────────────────────────────────────────

  const { data: authData, isLoading: authLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: authApi.me,
    retry: false,
  });

  if (!authLoading && !authData) {
    void navigate('/login');
    return null;
  }

  // ── Applications data ──────────────────────────────────────────────────────

  const { data: applications = [], isLoading } = useQuery({
    queryKey: ['applications'],
    queryFn: applicationApi.list,
    enabled: !!authData,
  });

  const { data: sankeyData } = useQuery({
    queryKey: ['applications', 'sankey'],
    queryFn: applicationApi.getSankeyData,
    enabled: !!authData,
  });

  // ── Filter + sort (client-side) ────────────────────────────────────────────

  const filtered = applications
    .filter((a) => filterAction === 'all' || a.lastAction === filterAction)
    .filter((a) => !filterAfter || a.appliedDate >= filterAfter)
    .sort((a, b) => {
      const cmp = a.appliedDate.localeCompare(b.appliedDate);
      return sortDir === 'desc' ? -cmp : cmp;
    });

  const hasFilters = filterAction !== 'all' || !!filterAfter;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-background">
      <Navigation />

      <main className="mx-auto max-w-6xl px-4 py-8">
        {/* Header */}
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Applications</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Track every job you've applied to. Click any cell to edit it.
            </p>
          </div>
          <Button size="sm" onClick={() => setAddOpen(true)} className="gap-1 shrink-0">
            <Plus className="h-4 w-4" />
            Add Application
          </Button>
        </div>

        {/* Sankey chart */}
        {sankeyData && (
          <div className="mb-6">
            <button
              onClick={() => setSankeyOpen((v) => !v)}
              className="mb-3 flex w-full items-center justify-between rounded-md px-1 py-1 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              <span>Pipeline Flow</span>
              {sankeyOpen
                ? <ChevronUp className="h-4 w-4" />
                : <ChevronDown className="h-4 w-4" />}
            </button>
            {sankeyOpen && (
              <Card>
                <CardContent className="pt-4 pb-3 px-4">
                  <ApplicationSankey data={sankeyData} height={260} />
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Filters */}
        <div className="mb-4 flex flex-wrap items-end gap-3">
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <SlidersHorizontal className="h-3.5 w-3.5" />
            <span>Filters:</span>
          </div>

          {/* Status filter */}
          <div className="space-y-0.5">
            <label className="text-xs text-muted-foreground">Status</label>
            <select
              value={filterAction}
              onChange={(e) => setFilterAction(e.target.value as ApplicationAction | 'all')}
              className="rounded-md border border-input bg-background px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="all">All statuses</option>
              {ALL_ACTIONS.map((a) => (
                <option key={a} value={a}>{actionLabel(a)}</option>
              ))}
            </select>
          </div>

          {/* Applied after */}
          <div className="space-y-0.5">
            <label className="text-xs text-muted-foreground">Applied after</label>
            <input
              type="date"
              value={filterAfter}
              onChange={(e) => setFilterAfter(e.target.value)}
              className="rounded-md border border-input bg-background px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          {/* Sort direction */}
          <div className="space-y-0.5">
            <label className="text-xs text-muted-foreground">Sort</label>
            <select
              value={sortDir}
              onChange={(e) => setSortDir(e.target.value as 'desc' | 'asc')}
              className="rounded-md border border-input bg-background px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="desc">Newest first</option>
              <option value="asc">Oldest first</option>
            </select>
          </div>

          {/* Clear filters */}
          {hasFilters && (
            <button
              onClick={() => { setFilterAction('all'); setFilterAfter(''); }}
              className="flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            >
              <X className="h-3 w-3" />
              Clear
            </button>
          )}

          {/* Count */}
          <span className="ml-auto text-xs text-muted-foreground">
            {filtered.length} {filtered.length === 1 ? 'application' : 'applications'}
          </span>
        </div>

        {/* Loading */}
        {isLoading && (
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        )}

        {/* Empty state */}
        {!isLoading && applications.length === 0 && (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-16 text-center">
            <ClipboardList className="mb-3 h-8 w-8 text-muted-foreground/50" />
            <p className="text-muted-foreground">No applications yet.</p>
            <Button
              variant="outline"
              size="sm"
              className="mt-4 gap-1"
              onClick={() => setAddOpen(true)}
            >
              <Plus className="h-4 w-4" />
              Add your first application
            </Button>
          </div>
        )}

        {/* No results after filter */}
        {!isLoading && applications.length > 0 && filtered.length === 0 && (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-10 text-center">
            <p className="text-muted-foreground text-sm">No applications match the current filters.</p>
            <button
              onClick={() => { setFilterAction('all'); setFilterAfter(''); }}
              className="mt-2 text-xs text-primary hover:underline"
            >
              Clear filters
            </button>
          </div>
        )}

        {/* Table */}
        {!isLoading && filtered.length > 0 && (
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/40">
                      <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Company</th>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Job Title</th>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap">Applied</th>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap">Status</th>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Notes</th>
                      <th className="px-4 py-2.5 w-10 text-center text-xs font-semibold uppercase tracking-wider text-muted-foreground">Post</th>
                      <th className="px-4 py-2.5 w-28" />
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((app) => (
                      <ApplicationRow key={app.id} application={app} allApplications={applications} />
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      <AddApplicationDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        onCreated={() => {}}
      />
    </div>
  );
}
